<template>
    <vx-expandable-card color="info">
        <template v-slot:content>
            <div class="flex flex-col gap-6">
                <div>
                    <span class="font-extrabold text-2xl text-slate-700">Membership Status Definitions</span>
                    <p class="text-sm text-slate-500 mb-0">
                        Configure how Intelligent Inbox treats membership status and steers AI replies.
                    </p>
                </div>

                <page-loading v-if="loading"></page-loading>

                <vx-alert v-if="!loading && loadError" color="danger" :closeable="false" shadow>
                    <div class="flex items-center justify-between">
                        Failed to load membership status definitions
                        <vx-button data-test="retry-btn" @click="fetchDefinitions" size="sm" color="danger">
                            Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                        </vx-button>
                    </div>
                </vx-alert>

                <template v-if="!loading && !loadError">
                    <vx-alert v-if="dirty" color="info" :closeable="false">
                        <div class="flex items-center justify-between gap-4">
                            <span>You have unsaved changes. Save to apply them.</span>
                            <vx-button
                                data-test="save-shortcut-btn"
                                size="sm"
                                color="info"
                                :loading="submitting"
                                @click.prevent="save"
                            >
                                Save
                            </vx-button>
                        </div>
                    </vx-alert>

                    <div v-if="definitions.length" class="flex flex-col gap-2">
                        <div ref="sortableEl" class="flex flex-col gap-2">
                            <div
                                v-for="(definition, index) in definitions"
                                :key="definition.id ?? definition.name + '-' + index"
                                class="membership-status-draggable flex items-center"
                            >
                                <span
                                    class="membership-status-handle pr-4 py-4"
                                    :class="submitting ? 'pointer-events-none opacity-50' : 'cursor-move'"
                                >
                                    <font-awesome-icon widthAuto :icon="faGripDotsVertical"></font-awesome-icon>
                                </span>
                                <membership-status-definition-card
                                    class="grow"
                                    :definition="definition"
                                    :disabled="submitting"
                                    @edit="openEdit(index)"
                                    @delete="removeDefinition(index)"
                                />
                            </div>
                        </div>
                    </div>

                    <div v-if="definitions.length < MAX_DEFINITIONS" class="flex">
                        <vx-box class="p-4">
                            <vx-button
                                data-test="new-definition-btn"
                                size="lg"
                                :disabled="submitting"
                                @click="openCreate"
                            >
                                New Definition
                                <font-awesome-icon :icon="faCirclePlus"></font-awesome-icon>
                            </vx-button>
                        </vx-box>
                    </div>

                    <vx-alert v-if="definitions.length >= MAX_DEFINITIONS" color="info" :closeable="false">
                        Maximum of {{ MAX_DEFINITIONS }} membership status definitions reached.
                    </vx-alert>

                    <vx-button
                        block
                        size="lg"
                        data-test="save-btn"
                        :loading="submitting"
                        :disabled="!dirty"
                        @click.prevent="save"
                    >
                        Save
                    </vx-button>
                </template>
            </div>
        </template>
    </vx-expandable-card>

    <modal-membership-status-definition
        v-model:visible="modalVisible"
        :definition="modalDefinition"
        :editIndex="modalEditIndex"
        @saved="onDefinitionSaved"
    />
</template>

<script lang="ts" setup>
import { faCirclePlus, faGripDotsVertical, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBox, VxButton, VxExpandableCard, VxSwal } from '@voxie/frontend-components';
import { moveArrayElement, useSortable } from '@vueuse/integrations/useSortable';
import { computed, nextTick, ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useToasted } from '~/composables/useToasted';
import { membershipStatusDefinitionsClient } from '~/services';
import type {
    MembershipStatusDefinitionFormData,
    MembershipStatusDefinitionType,
} from '~/services/membershipStatusDefinitions/membershipStatusDefinitionsClient.types';
import { catchRequestError } from '~/utils/request';
import MembershipStatusDefinitionCard from './MembershipStatusDefinitionCard.vue';
import ModalMembershipStatusDefinition from './ModalMembershipStatusDefinition.vue';

const MAX_DEFINITIONS = 20;
const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const loading = ref(false);
const loadError = ref(false);
const submitting = ref(false);
const definitions = ref<MembershipStatusDefinitionType[]>([]);
const originalDefinitions = ref<MembershipStatusDefinitionType[]>([]);

const modalVisible = ref(false);
const modalDefinition = ref<MembershipStatusDefinitionType | null>(null);
const modalEditIndex = ref<number | null>(null);

const toFormData = (definition: MembershipStatusDefinitionType): MembershipStatusDefinitionFormData => ({
    name: definition.name,
    classification_steering: definition.classification_steering,
    message_steering: definition.message_steering,
});

const serialize = (list: MembershipStatusDefinitionType[]): string => JSON.stringify(list.map(toFormData));

const dirty = computed(() => serialize(definitions.value) !== serialize(originalDefinitions.value));

const openCreate = (): void => {
    modalDefinition.value = null;
    modalEditIndex.value = null;
    modalVisible.value = true;
};

const openEdit = (index: number): void => {
    modalDefinition.value = definitions.value[index] ?? null;
    modalEditIndex.value = index;
    modalVisible.value = true;
};

const removeDefinition = (index: number): void => {
    const definition = definitions.value[index];

    VxSwal.confirmation(
        'Are you sure?',
        `Are you sure you want to delete "${definition?.name}"?`,
        'danger',
        async () => {
            definitions.value = definitions.value.filter((_, i) => i !== index);
        }
    );
};

const onDefinitionSaved = (data: MembershipStatusDefinitionFormData, editIndex: number | null): void => {
    if (null !== editIndex && undefined !== definitions.value[editIndex]) {
        definitions.value = definitions.value.map((definition, i) =>
            i === editIndex ? { ...definition, ...data } : definition
        );
    } else {
        definitions.value = [...definitions.value, { ...data } as MembershipStatusDefinitionType];
    }
    modalVisible.value = false;
};

const save = async (): Promise<void> => {
    if (submitting.value || !dirty.value) {
        return;
    }

    submitting.value = true;

    try {
        const response = await membershipStatusDefinitionsClient.updateMembershipStatusDefinitions(
            { teamId },
            { definitions: definitions.value.map(toFormData) }
        );
        definitions.value = response.data.definitions;
        originalDefinitions.value = response.data.definitions.map((definition) => ({ ...definition }));
        toasted.success('Membership status definitions saved');
    } catch (err) {
        toasted.error(catchRequestError(err));
    } finally {
        submitting.value = false;
    }
};

const sortableEl = ref<HTMLElement | null>(null);

const { start: startSortable } = useSortable(sortableEl, definitions, {
    handle: '.membership-status-handle',
    draggable: '.membership-status-draggable',
    animation: 200,
    onUpdate: (e: { oldIndex: number; newIndex: number }) => {
        moveArrayElement(definitions.value, e.oldIndex, e.newIndex);
    },
} as Record<string, unknown>);

watch(loading, async (isLoading) => {
    if (!isLoading && !loadError.value) {
        await nextTick();
        startSortable();
    }
});

const fetchDefinitions = async (): Promise<void> => {
    loading.value = true;
    loadError.value = false;

    try {
        const response = await membershipStatusDefinitionsClient.getMembershipStatusDefinitions({ teamId });
        definitions.value = response.data.definitions;
        originalDefinitions.value = response.data.definitions.map((definition) => ({ ...definition }));
    } catch {
        loadError.value = true;
    } finally {
        loading.value = false;
    }
};

fetchDefinitions();
</script>
